import { useEffect, useRef, useState } from "react";
import { ArrowUp } from "lucide-react";
import styled from "styled-components";
import ProcurementChatbot from "../procurementChatbot";
import {
  ChatBotAvatarCTA,
  ChatbotHeader,
} from "./ChatbotShellParts";
import SiteHeader from "../../pages/landing/components/Header/SiteHeader";
import ChatBg from "../../assets/pages/landing/bg.png";
import closeIcon from "../../assets/pages/landing/close.svg";
import expandIcon from "../../assets/pages/landing/expand.svg";

const CHATBOT_OVERLAY_Z_INDEX = 25000;

const GlobalChatbot = () => {
  const [chatOpen, setChatOpen] = useState(false);
  const [expanded, setIsExpanded] = useState(false);
  const launcherRef = useRef(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [sharedChatbotInput, setSharedChatbotInput] = useState([]);
  const [sharedChatbotOutput, setSharedChatbotOutput] = useState([]);
  const [sharedChatTimestamps, setSharedChatTimestamps] = useState([]);
  const [sharedResponseTimestamps, setSharedResponseTimestamps] = useState([]);

  const sharedChatProps = {
    sharedChatbotInput,
    setSharedChatbotInput,
    sharedChatbotOutput,
    setSharedChatbotOutput,
    sharedChatTimestamps,
    setSharedChatTimestamps,
    sharedResponseTimestamps,
    setSharedResponseTimestamps,
  };

  useEffect(() => {
    const updateVisibility = () => {
      setShowBackToTop(window.scrollY > window.innerHeight * 0.5);
    };
    updateVisibility();
    window.addEventListener("scroll", updateVisibility, { passive: true });
    window.addEventListener("resize", updateVisibility);
    return () => {
      window.removeEventListener("scroll", updateVisibility);
      window.removeEventListener("resize", updateVisibility);
    };
  }, []);

  const handleBackToTop = () => {
    const target = document.getElementById("main-content") || document.querySelector("main");
    if (target) {
      if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  };

  useEffect(() => {
    const openChatbot = () => {
      setChatOpen(true);
    };

    window.addEventListener("open-global-chatbot", openChatbot);

    return () => {
      window.removeEventListener("open-global-chatbot", openChatbot);
    };
  }, []);

  useEffect(() => {
    if (!chatOpen || !expanded) {
      return undefined;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [chatOpen, expanded]);

  const chatbotDialogRef = useRef(null);
  const launcherTriggerRef = useRef(null);

  useEffect(() => {
    if (!chatOpen) {
      if (launcherTriggerRef.current) {
        launcherTriggerRef.current.focus?.();
      }
      return;
    }

    if (document.activeElement && document.activeElement !== document.body) {
      launcherTriggerRef.current = document.activeElement;
    }

    const timer = setTimeout(() => {
      if (chatbotDialogRef.current) {
        const closeBtn = chatbotDialogRef.current.querySelector('button[aria-label*="Close"]');
        if (closeBtn) {
          closeBtn.focus();
        } else {
          const focusable = chatbotDialogRef.current.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
          if (focusable.length > 0) {
            focusable[0].focus();
          }
        }
      }
    }, 50);

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        dismissChat();
        return;
      }

      if (event.key === "Tab" && chatbotDialogRef.current) {
        const focusableElements = chatbotDialogRef.current.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        const focusable = Array.from(focusableElements).filter(
          (el) => el.offsetParent !== null && !el.hasAttribute("disabled")
        );
        if (focusable.length === 0) return;

        const firstElement = focusable[0];
        const lastElement = focusable[focusable.length - 1];

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [chatOpen, expanded]);

  const dismissChat = () => {
    setChatOpen(false);
    setIsExpanded(false);
  };

  const handleClose = () => {
    dismissChat();
  };

  const handleExpansion = () => {
    setIsExpanded((previous) => !previous);
  };

  if (chatOpen && expanded) {
    return (
      <FullscreenChatWrapper
        ref={chatbotDialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="NMDFC Virtual Assistant - MIRA"
        tabIndex={-1}
      >
        <FullscreenSiteHeader onNavigate={dismissChat} />
        <FullscreenChatContent>
          <ProcurementChatbot
            expanded={true}
            embeddedExpanded
            onClose={handleClose}
            onExpandMin={handleExpansion}
            {...sharedChatProps}
          />
        </FullscreenChatContent>
      </FullscreenChatWrapper>
    );
  }

  if (chatOpen) {
    return (
      <ChatbotFAB>
        <ChatbotWindow
          ref={chatbotDialogRef}
          role="dialog"
          aria-modal="true"
          aria-label="NMDFC Virtual Assistant - MIRA"
          tabIndex={-1}
        >
          <ChatbotHeader>
            <button
              type="button"
              onClick={handleExpansion}
              aria-label="Expand chat to full screen"
              title="Expand chat"
            >
              <img src={expandIcon} alt="" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close chat assistant"
              title="Close chat"
            >
              <img src={closeIcon} alt="" aria-hidden="true" />
            </button>
          </ChatbotHeader>
          <ChatbotWrapper>
            <ProcurementChatbot
              expanded={false}
              onClose={handleClose}
              onExpandMin={handleExpansion}
              {...sharedChatProps}
            />
          </ChatbotWrapper>
        </ChatbotWindow>
      </ChatbotFAB>
    );
  }

  const launcher = (
    <ChatbotFAB ref={launcherRef} $launcher data-global-assistant-launcher>
      {showBackToTop && (
        <BackToTopButton
          id="back-to-top"
          type="button"
          onClick={handleBackToTop}
          aria-label="Back to top"
          title="Back to top"
        >
          <ArrowUp size={17} strokeWidth={2.4} aria-hidden="true" />
          <span>Back to top</span>
        </BackToTopButton>
      )}
      <ChatBotAvatarCTA onOpen={() => setChatOpen(true)} chatOpen={chatOpen} />
    </ChatbotFAB>
  );
  return launcher;
};

const ChatbotFAB = styled.div`
  --assistant-navbar-offset: 132px;

  position: fixed;
  right: 24px;
  bottom: var(--launcher-bottom, max(20px, env(safe-area-inset-bottom)));
  z-index: ${CHATBOT_OVERLAY_Z_INDEX};
  display: flex;
  align-items: flex-end;
  gap: 4px;
  pointer-events: none;

  ${({ $launcher }) => $launcher && `
    right: 8px;
    width: clamp(76px, 16vw, 180px);
    max-width: clamp(76px, 16vw, 180px);
    flex-direction: column;
    align-items: center;

    [data-chatbot-avatar] { width: 100%; }
    img { width: 100%; height: auto; }
  `}

  @media (max-width: 768px) {
    --assistant-navbar-offset: 112px;
    right: 8px;
    max-width: calc(100vw - 32px);
  }

  @media (max-height: 680px) {
    bottom: var(--launcher-bottom, max(12px, env(safe-area-inset-bottom)));
  }

  @media (max-width: 767px), (max-height: 600px) {
    right: 8px;
    max-width: calc(100vw - 24px);
    ${({ $launcher }) => $launcher && `
      bottom: max(12px, env(safe-area-inset-bottom));
      flex-direction: column;
      width: clamp(76px, 16vw, 180px);
      min-width: 0;
      max-width: calc(100vw - 24px);

      [data-chatbot-avatar] {
        width: 100%;
        container-type: inline-size;
      }

      img {
        width: 100%;
        height: auto;
      }


    `}
  }
  @media (max-width: 480px), (max-height: 360px) {
    ${({ $launcher }) => $launcher && `
      width: clamp(64px, 20vw, 96px);
      max-width: clamp(64px, 20vw, 96px);
      gap: 4px;

      [data-chatbot-avatar] { width: 100%; }
      [data-chatbot-label] {
        font-size: 10px;
        line-height: 1.2;
        padding: 3px;
      }
      #back-to-top {
        font-size: 10px;
        line-height: 1.2;
        padding: 5px;
        margin-bottom: 4px;
        gap: 3px;
      }
      #back-to-top svg { flex-shrink: 0; width: 12px; }
    `}
  }

`;

const BackToTopButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 18px;
  padding: 9px 12px;
  border: 1px solid rgba(15, 138, 126, 0.55);
  border-radius: 999px;
  background: linear-gradient(135deg, #ffffff 0%, #eef8f6 100%);
  color: #0f5c66;
  box-shadow: 0 8px 22px rgba(15, 92, 102, 0.18);
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  pointer-events: auto;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, #eef8f6 0%, #dff3ef 100%);
    box-shadow: 0 10px 26px rgba(15, 92, 102, 0.24);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid #0f5c66;
    outline-offset: 3px;
  }

  @media (max-width: 480px) {
    margin-bottom: 10px;
    padding: 8px 10px;
    font-size: 11px;
  }
`;

const ChatbotWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 12px;

  @media (max-width: 480px), (max-height: 680px) {
    padding: 8px;
  }
`;

const FullscreenChatWrapper = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${CHATBOT_OVERLAY_Z_INDEX};

  background-color: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 767px), (max-height: 600px) {
    overflow-y: auto;
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;

    background-image: url(${ChatBg});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.2;

    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const FullscreenSiteHeader = styled(SiteHeader)`
  flex-shrink: 0;
  position: relative;
  z-index: 2;
`;

const FullscreenChatContent = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  padding-top: clamp(44px, 3vw, 52px);
  overflow: hidden;

  > * {
    min-height: 0;
    max-height: 100%;
  }

  @media (max-width: 1024px) {
    padding-top: 0;
  }

  @media (max-width: 767px), (max-height: 600px) {
    flex: 1 0 auto;
    height: max(400px, 100dvh);
    min-height: 400px;
  }
`;

const ChatbotWindow = styled.div`
  width: min(600px, calc(100vw - 48px));
  height: min(600px, calc(100dvh - 48px));
  min-width: 0;
  min-height: 0;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  pointer-events: auto;

  &::before {
    content: "";
    position: absolute;
    inset: 0;

    background-image: url(${ChatBg});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.2;

    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 480px) {
    width: calc(100vw - 32px);
    height: calc(100dvh - 48px - env(safe-area-inset-bottom));
    border-radius: 12px;
  }

  @media (max-height: 680px) {
    height: calc(100dvh - 24px - env(safe-area-inset-bottom));
  }
`;

export default GlobalChatbot;
